import { useCallback } from 'react';

import type { ExtractedFact, FactRef, PreviewedFact } from '@karehero/models';

import { usePreviewFactsMutation } from 'store/api/inference';

import { useLiveCheck } from './useLiveCheck';

const NO_FACTS: readonly ExtractedFact[] = [];

/** Read as a sent message's facts are, though nothing is stored. */
const asExtracted = (fact: PreviewedFact): ExtractedFact => ({
  id: `preview:${fact.topicSlug}:${fact.factName}`,
  topicSlug: fact.topicSlug,
  factName: fact.factName,
  factValue: fact.factValue,
  mappings: [],
});

/** Everything found so far, the newest value for each fact. */
export const withNewest = (
  shown: readonly ExtractedFact[],
  found: readonly ExtractedFact[],
): readonly ExtractedFact[] => {
  const foundIDs = new Set(found.map((f) => f.id));
  return [...shown.filter((f) => !foundIDs.has(f.id)), ...found];
};

export interface UseFactPreviewOptions {
  careCircleId?: string;
  facts: readonly FactRef[];
  /** What has been typed or said but not yet sent, live words included. */
  said: string;
  /** What is being answered, and where from, as it will be sent. */
  context: string;
  source: string;
  isEnabled: boolean;
}

/**
 * useFactPreview reads what has not yet been sent for the facts it would
 * give, so they can be shown before it is. Nothing is stored: change or take
 * back the words and the facts they gave go with them.
 */
export const useFactPreview = ({
  careCircleId,
  facts,
  said,
  context,
  source,
  isEnabled,
}: UseFactPreviewOptions): readonly ExtractedFact[] => {
  const [previewFacts] = usePreviewFactsMutation();
  const check = useCallback(
    async (text: string) => {
      const { facts: found } = await previewFacts({
        careCircleId: careCircleId ?? '',
        body: { said: text, context, source, facts: [...facts] },
      }).unwrap();
      return found.filter((f) => f.status === 'found').map(asExtracted);
    },
    [careCircleId, context, facts, previewFacts, source],
  );
  return useLiveCheck({
    text: said,
    isEnabled: isEnabled && !!careCircleId,
    initial: NO_FACTS,
    check,
    keepFurthest: withNewest,
  }).value;
};
